import { apiSlice } from '../../api/apiSlice';
import * as constants from '../../constants';

export const CoachingAction = apiSlice.injectEndpoints({
    endpoints: (builder: any) => ({
        // Start new coaching action plan (creates action plan + first session)
        startCoachingActionPlan: builder.mutation({
            query: (arg: any) => ({
                url: constants.START_COACHING_ACTION_PLAN,
                method: 'POST',
                body: arg,
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            }),
            // Invalidate session data AND the associate metrics cache so session_count
            // is re-fetched if the user returns to Prepare after starting a plan.
            invalidatesTags: ['CoachingActionSession', 'PrepareAssociateMetrics'],
        }),

        // Create additional session for existing action plan
        createAdditionalSession: builder.mutation({
            query: (arg: any) => ({
                url: constants.CREATE_ADDITIONAL_SESSION,
                method: 'POST',
                body: arg,
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            }),
            invalidatesTags: ['CoachingActionSession'],
        }),

        // Get session by ID
        getSessionById: builder.query({
            query: (sessionId: number) => ({
                url: `${constants.GET_SESSION_BY_ID}/${sessionId}`,
            }),
            providesTags: ['CoachingActionSession'],
        }),

        // Save FUEL notes (Frame, Understand, Explore)
        saveFuelNotes: builder.mutation({
            query: (arg: any) => ({
                url: constants.SAVE_FUEL_NOTES,
                method: 'POST',
                body: arg,
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            }),
            invalidatesTags: ['CoachingActionSession'],
        }),

        // Get historical (closed) action plans for an associate (AssociateCAP tab 2)
        getAssociateHistoricalActionPlans: builder.query({
            query: ({
                empNo,
                startDate,
                endDate,
            }: {
                empNo: string;
                startDate: string;
                endDate: string;
            }) => ({
                url: constants.GET_ASSOCIATE_HISTORICAL_ACTION_PLANS,
                params: { empNo, startDate, endDate },
            }),
            providesTags: ['AssociateHistoricalActionPlans'],
        }),

        // Get active action plans for an associate (AssociateCAP tab 1)
        getAssociateActiveActionPlans: builder.query({
            query: (empNo: string) => ({
                url: constants.GET_ASSOCIATE_ACTIVE_ACTION_PLANS,
                params: { empNo },
            }),
            providesTags: ['AssociateActiveActionPlans'],
        }),

        // Get rolling 30-day metric summary for an associate (AssociateCAP top cards)
        getRollingMetricSummary: builder.query({
            query: (empNo: string) => ({
                url: constants.GET_ROLLING_METRIC_SUMMARY,
                params: { empNo },
            }),
            providesTags: ['RollingMetricSummary'],
        }),

        // Update Layout section
        updateLayout: builder.mutation({
            query: (arg: any) => ({
                url: constants.UPDATE_LAYOUT,
                method: 'PUT',
                body: arg,
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            }),
            invalidatesTags: ['CoachingActionSession'],
        }),
        getSurveyQuestions: builder.query({
            query: () => ({
                url: constants.GET_SURVEY_QUESTIONS,
            }),
            providesTags: ['CoachingSurveyQuestions'],
        }),

        submitSurveyResponse: builder.mutation({
            query: (arg: any) => ({
                url: constants.SUBMIT_SURVEY_RESPONSE,
                method: 'POST',
                body: arg,
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            }),
        }),
        updateSessionOverride: builder.mutation({
            query: (arg: any) => ({
                url: constants.UPDATE_SESSION_OVERRIDE,
                method: 'PUT',
                body: arg,
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            }),
            invalidatesTags: ['CoachingActionSession', 'CoachingActionPlan'],
        }),
    }),
});

export const {
    useStartCoachingActionPlanMutation,
    useCreateAdditionalSessionMutation,
    useGetSessionByIdQuery,
    useSaveFuelNotesMutation,
    useUpdateLayoutMutation,
    useGetSurveyQuestionsQuery,
    useSubmitSurveyResponseMutation,
    useGetRollingMetricSummaryQuery,
    useGetAssociateActiveActionPlansQuery,
    useGetAssociateHistoricalActionPlansQuery,
    useUpdateSessionOverrideMutation,
} = CoachingAction;
